import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MapRef } from 'react-map-gl';
import { getMapDefaultConfig } from 'components/map/atoms';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import { WELL_STATUS_COLORS, wellsLayerOptions } from './well-statuses';
import type { WellFilters } from './well-filters';

export const mapConfigAtom = atomWithStorage(
  // Bumped on every default change — a stored config wins over the default.
  // v2 moved scroll zoom on; v3 shows wells from the start.
  'insights-area-geometries-show-map-config-v3',
  getMapDefaultConfig({
    // Wells on from the start, so the legend describes what the map is painting.
    // The shared `wells_v2` tiles are minzoom 10, so a large area shows none until zoomed.
    wellsV2: {
      checked: true,
      options: wellsLayerOptions(true),
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    scrollZoom: {
      checked: true,
    },
    popups: {
      checked: false,
    },
  })
);

/** A handle on the live map, so the reset-zoom control can move the camera. */
export const mapRefAtom = atom<MapRef | null>(null);

// Per-status well colours as the legend has them, keyed by the status the query returns.
// Seeded from WELL_STATUS_COLORS and persisted, so a picked colour survives a reload.
export const wellStatusColorsAtom = atomWithStorage<Record<string, string>>(
  'insights-area-geometries-well-status-colors-v1',
  WELL_STATUS_COLORS
);

// The selected table row, by UWI — drives the row and the map halo, so they stay in
// step. Not persisted: a stale pick would highlight a bore the boundary may not catch.
export const selectedWellUwiAtom = atom<string | null>(null);

// Which wells the page shows, by attribute. Persisted alongside the colour rules — both
// are the user's own view settings rather than anything about the area.
export const areaWellFiltersAtom = atomWithStorage<WellFilters>(
  'insights-area-geometries-well-filters-v1',
  {}
);

// The colour-by rules for this page's map. Persisted like the map config: the rules are
// the user's own view settings, not part of the area.
export const areaColorRulesAtom = atomWithStorage<ColorByRule[]>(
  'insights-area-geometries-color-rules-v1',
  []
);

// Paint membership, `${attribute}:${value}` → UWIs, grouped client-side from the facet
// call so switching attribute costs no round trip. Not persisted — it is derived.
export const areaColorByUwiMapAtom = atom<Record<string, string[]>>({});
